import { cast, flow, getSnapshot, types } from 'mobx-state-tree';
import isEqual from 'lodash/isEqual';
import { mergeSignalMappings } from '../utils/mapping-builders';
import { Crystal } from 'http-requests';
import { RequestStateModel } from '../../general-models';

const SignalMappingModel = types
  .model('SignalMappingModel', {
    mapping_id: types.optional(types.string, ''),
    event_type: types.optional(types.string, ''),
    category: types.optional(types.maybeNull(types.string), null),
    selected: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    changeField<K extends keyof typeof self>(field: K, value: typeof self[K]) {
      self[field] = value;
    },
  }));

export const SignalMappingsModel = types
  .model('SignalMappingsModel', {
    mappings: types.optional(types.array(SignalMappingModel), []),
    editedMappings: types.optional(types.array(SignalMappingModel), []),
    isEdit: types.optional(types.boolean, false),
    status: types.optional(RequestStateModel, {}),
  })
  .views((self) => ({
    get isModified() {
      const mappedWithoutSelection = getSnapshot(self.mappings).map(
        ({ selected, ...rest }) => rest
      );
      const editedWithoutSelection = getSnapshot(self.editedMappings).map(
        ({ selected, ...rest }) => rest
      );

      return !isEqual(mappedWithoutSelection, editedWithoutSelection);
    },
    get data() {
      return self.isEdit ? self.editedMappings : self.mappings;
    },
    get selectedCount() {
      return self.editedMappings.filter((item) => item.selected).length;
    },
  }))
  .actions((self) => {
    const syncEditedMappings = () => {
      self.editedMappings = cast(getSnapshot(self.mappings));
    };
    const setAllSelected = (selected: boolean) => {
      self.editedMappings.forEach((item) => {
        item.selected = selected;
      });
    };

    return {
      startEdit() {
        self.isEdit = true;
        syncEditedMappings();
      },
      cancelEdit() {
        self.isEdit = false;
        syncEditedMappings();
      },

      hydrate(
        events: Crystal.DirectoryDTO['events'],
        mappingsResponse: Crystal.EventMappingDTO
      ) {
        self.mappings = cast(
          mergeSignalMappings({
            events,
            mappings: mappingsResponse.mappings || [],
          })
        );
        syncEditedMappings();
      },
      clear() {
        self.isEdit = false;
        self.mappings.clear();
        syncEditedMappings();
      },
      save: flow(function* (id: string) {
        if (!self.isModified) {
          return;
        }

        try {
          self.status.loadingStates();

          const payload: Crystal.EventMappingPayloadDTO = {
            mappings: getSnapshot(self.editedMappings)
              .filter((map) => map.category)
              .map(({ selected, ...rest }) => rest),
          };

          setAllSelected(false);

          if (payload.mappings.length) {
            yield Crystal.SetEventCategoryMappings(id, payload);
          }

          self.mappings = cast(
            self.mappings.map((item) => {
              const finded = payload.mappings.find(
                (el) => el.event_type === item.event_type
              );

              if (finded) {
                return { ...finded };
              }

              return item;
            })
          );

          syncEditedMappings();
          self.status.successStates();
        } catch (error) {
          console.error(error);
          self.status.errorStates();

          return Promise.reject(error);
        } finally {
          self.status.finallyStates();
        }
      }),
    };
  });
